import log from '../utils/logger';
// Costanti per le azioni dei messaggi
export const POST_MESSAGE_ACTIONS = {
    // Messaggi in uscita (dall'app al parent window)
    CLOSE_CASINO_WEB: 'closeCasinoWeb',
    LOGIN_FOR_CASINO: 'login4casino',
    CASINO_LOGOUT: 'casino4logout',

    // Messaggi in uscita (dall'app all'iframe del gioco)
    CLOSE_GAME_SESSION: 'manualCloseSession',
    CUSTOM_CLOSE: 'customclose',

    // Messaggi in entrata (dall'iframe all'app)
    LOGIN: 'login',
} as const;

export type PostMessageAction = typeof POST_MESSAGE_ACTIONS[keyof typeof POST_MESSAGE_ACTIONS];

interface PostMessagePayload {
    action: PostMessageAction;
    params?: string;
    [key: string]: any;
}

type MessageHandler = (event: MessageEvent<PostMessagePayload>) => void;

class PostMessageHandler {
    private handlers: Map<PostMessageAction | '*', MessageHandler[]> = new Map();
    private logAllMessages: boolean = true;
    private iframeRef: React.RefObject<HTMLIFrameElement> | null = null;

    constructor() {
        this.initializeListener();
    }

    /**
     * Imposta il riferimento all'iframe per l'invio di messaggi
     */
    public setIframeRef(ref: React.RefObject<HTMLIFrameElement> | null): void {
        this.iframeRef = ref;
    }

    private initializeListener(): void {
        window.addEventListener('message', this.handleMessage.bind(this));
    }

    private handleMessage(event: MessageEvent): void {
        // Log all messages if enabled
        if (this.logAllMessages) {
            log.debug('📨 PostMessage ricevuto:', {
                origin: event.origin,
                data: event.data,
                source: event.source,
                timestamp: new Date().toISOString()
            });
        }

        let payload: PostMessagePayload | null = null;

        // Gestisci sia stringhe JSON che oggetti
        if (event.data) {
            if (typeof event.data === 'string') {
                try {
                    const parsed = JSON.parse(event.data);
                    if (parsed && typeof parsed === 'object' && 'action' in parsed) {
                        payload = parsed as PostMessagePayload;
                    }
                } catch (e) {
                    // Non è un JSON valido, ignora
                }
            } else if (typeof event.data === 'object' && 'action' in event.data) {
                payload = event.data as PostMessagePayload;
            }
        }

        // Se abbiamo un payload valido, esegui gli handler
        if (payload) {
            // Esegui handler specifici per l'azione
            const actionHandlers = this.handlers.get(payload.action) || [];
            const universalHandlers = this.handlers.get('*') || [];
            
            [...actionHandlers, ...universalHandlers].forEach(handler => {
                try {
                    handler(event as MessageEvent<PostMessagePayload>);
                } catch (error) {
                    log.error('Errore nell\'handler postMessage:', error);
                }
            });
        }
    }

    /**
     * Registra un handler per una specifica azione
     * @param action - L'azione da intercettare (* per tutte)
     * @param handler - La funzione da eseguire quando arriva un messaggio con quell'azione
     */
    public on(action: PostMessageAction | '*', handler: MessageHandler): void {
        if (!this.handlers.has(action)) {
            this.handlers.set(action, []);
        }
        this.handlers.get(action)!.push(handler);
    }

    /**
     * Rimuove tutti gli handler per una specifica azione
     * @param action - L'azione per cui rimuovere gli handler
     */
    public off(action: PostMessageAction | '*'): void {
        this.handlers.delete(action);
    }

    /**
     * Invia un messaggio all'iframe con un'azione specifica
     * @param action - L'azione del messaggio
     * @param data - I dati da inviare
     * @param targetOrigin - L'origine target (default: '*')
     */
    public send(action: PostMessageAction, data?: any, targetOrigin: string = '*'): void {
        if (this.iframeRef?.current?.contentWindow) {
            const payload: PostMessagePayload = {
                action,
                data,
                timestamp: new Date().toISOString()
            };
            
            this.iframeRef.current.contentWindow.postMessage(payload, targetOrigin);
            
            if (this.logAllMessages) {
                log.debug('📤 PostMessage inviato:', {
                    payload,
                    targetOrigin,
                    timestamp: payload.timestamp
                });
            }
        } else {
            console.warn('Iframe non disponibile per l\'invio del messaggio');
        }
    }

    /**
     * Invia un messaggio generico a una finestra specifica
     * @param target - Il riferimento alla finestra
     * @param payload - Il payload del messaggio
     * @param targetOrigin - L'origine target (default: '*')
     */
    public sendToWindow(target: Window | null, payload: PostMessagePayload, targetOrigin: string = '*'): void {
        if (target) {
            target.postMessage(payload, targetOrigin);
            
            if (this.logAllMessages) {
                log.debug('📤 PostMessage inviato:', {
                    payload,
                    targetOrigin,
                    timestamp: new Date().toISOString()
                });
            }
        }
    }

    /**
     * Invia un messaggio al parent window
     * @param action - L'azione del messaggio
     * @param params - Parametri aggiuntivi
     * @param targetOrigin - L'origine target (default: '*')
     */
    public sendToParent(action: PostMessageAction, params?: string | Record<string, any>, targetOrigin: string = '*'): void {
        const payload: PostMessagePayload = {
            action
        };

        if (params) {
            if (typeof params === 'string') {
                payload.params = params;
            } else {
                Object.assign(payload, params);
            }
        }

        window.parent.postMessage(payload, targetOrigin);
        
        if (this.logAllMessages) {
            log.debug('📤 PostMessage inviato al parent:', {
                payload,
                targetOrigin,
                timestamp: new Date().toISOString()
            });
        }
    }

    /**
     * Abilita/disabilita il logging di tutti i messaggi
     * @param enabled - true per abilitare, false per disabilitare
     */
    public setLogging(enabled: boolean): void {
        this.logAllMessages = enabled;
    }

    /**
     * Cleanup: rimuove il listener globale
     */
    public cleanup(): void {
        window.removeEventListener('message', this.handleMessage.bind(this));
        this.handlers.clear();
        this.iframeRef = null;
    }
}

// Esporta un'istanza singleton
export const postMessageHandler = new PostMessageHandler();

// Esporta anche la classe per chi volesse creare istanze multiple
export default PostMessageHandler;